import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, LogIn, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SHELL_LOGIN_URL, fetchAuthMe, setAuthToken } from '@/api/auth';
import { useAuthStore } from '@/stores/useAuthStore';

export function LoginPage() {
  const returnUrl = encodeURIComponent(window.location.origin);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const { isAuthenticated, hydrate } = useAuthStore();
  const navigate = useNavigate();

  // Read and clear any auth error from bootstrap
  const [authError, setAuthError] = useState(() => {
    const err = sessionStorage.getItem('apx_auth_error');
    if (err) sessionStorage.removeItem('apx_auth_error');
    return err;
  });

  // Retry auth exchange without redirecting to Shell
  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    setAuthError(null);
    try {
      const result = await fetchAuthMe();
      setAuthToken(result.token);
      hydrate();
      navigate('/sessions', { replace: true });
    } catch {
      setAuthError('Could not authenticate. Please sign in again.');
    } finally {
      setIsRetrying(false);
    }
  }, [navigate, hydrate]);

  // Redirect if already authenticated (e.g., browser back button)
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/sessions', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Brain className="h-12 w-12 text-primary" />
          </div>
          <CardTitle className="text-2xl text-foreground">APX Respond</CardTitle>
          <p className="text-muted-foreground mt-2">
            Pre-populate DDQs and RFPs from APX Vault data
          </p>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4">
          {authError && (
            <div className="w-full space-y-2">
              <div className="text-sm text-destructive text-center p-2 bg-destructive/10 rounded">
                {authError}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                disabled={isRetrying}
                onClick={handleRetry}
              >
                {isRetrying ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                    Retrying...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-3.5 w-3.5 mr-2" />
                    Try again
                  </>
                )}
              </Button>
            </div>
          )}
          <Button
            size="lg"
            className="w-full"
            disabled={isRedirecting}
            onClick={() => {
              setIsRedirecting(true);
              window.location.href = `${SHELL_LOGIN_URL}?returnUrl=${returnUrl}`;
            }}
          >
            {isRedirecting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Redirecting to APX Platform...
              </>
            ) : (
              <>
                <LogIn className="h-4 w-4 mr-2" />
                Sign in with APX Platform
              </>
            )}
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            You will be redirected to the APX Platform to sign in.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
